import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Store } from '@ngrx/store';
import { selectCurrentUser } from 'pl-core-services';
import { environment } from '@environments/environment';
import { getIsSiriusEnabled } from '@app/common/sirius-util';

interface LegacyZendeskResponse {
  jwt: string;
  exists_in_zendesk: boolean;
}

interface TokenExchangeResponse {
  access_token: string;
  issued_token_type: string;
  token_type: string;
  expires_in: number;
  scope: string;
}

export interface ZendeskAuthResult {
  token: string;
  isLegacy: boolean;
  existsInZendesk: boolean;
}

@Injectable({ providedIn: 'root' })
export class ZendeskAuthService {
  private currentUser$ = this.store.select(selectCurrentUser);

  constructor(
    private http: HttpClient,
    private oidcSecurityService: OidcSecurityService,
    private store: Store
  ) {}

  authenticateZendesk(): Observable<ZendeskAuthResult | null> {
    if (getIsSiriusEnabled()) {
      return this.siriusTokenExchange();
    }
    return this.legacyAuth();
  }

  openSupportChat(): void {
    const zenDeskWidget = (window as any).zE;
    if (!zenDeskWidget) {
      return;
    }

    const { contactEmail, contactName } =
      environment.zendesk.webWidget.conversationFields;

    this.currentUser$
      .pipe(
        filter(
          (user) => !!user?.email && !!user?.first_name && !!user?.last_name
        ),
        take(1),
        switchMap((currentUser) =>
          this.authenticateZendesk().pipe(
            map((result) => ({ currentUser, result }))
          )
        )
      )
      .subscribe(({ currentUser, result }) => {
        if (result && (!result.isLegacy || result.existsInZendesk)) {
          zenDeskWidget(
            'messenger',
            'loginUser',
            (callback: (arg: any) => void) => {
              callback(result.token);
            }
          );
          zenDeskWidget('messenger:set', 'conversationFields', [
            { id: contactEmail, value: currentUser.email },
            {
              id: contactName,
              value: `${currentUser.first_name} ${currentUser.last_name}`,
            },
          ]);
        }
        zenDeskWidget('messenger', 'show');
        zenDeskWidget('messenger', 'open');
      });
  }

  private legacyAuth(): Observable<ZendeskAuthResult | null> {
    if (!environment.zendesk.webWidget?.authUrl) {
      // eslint-disable-next-line no-console
      console.warn('Zendesk auth URL not configured');
      return of(null);
    }

    return this.http
      .get<LegacyZendeskResponse>(environment.zendesk.webWidget.authUrl)
      .pipe(
        switchMap((resp) =>
          of({
            token: resp.jwt,
            isLegacy: true,
            existsInZendesk: resp.exists_in_zendesk,
          })
        ),
        catchError((err) => {
          // eslint-disable-next-line no-console
          console.error('Zendesk legacy auth failed', err);
          return of(null);
        })
      );
  }

  private siriusTokenExchange(): Observable<ZendeskAuthResult | null> {
    if (!environment.zendesk.webWidget?.tokenExchangeUrl) {
      // eslint-disable-next-line no-console
      console.warn('Zendesk token exchange URL not configured');
      return of(null);
    }

    return this.oidcSecurityService.getAccessToken().pipe(
      switchMap((accessToken) => {
        const tokenExchangeUrl = environment.zendesk.webWidget.tokenExchangeUrl;
        const clientId = environment.oidc_client_id;

        const body = new URLSearchParams();
        body.set(
          'grant_type',
          'urn:ietf:params:oauth:grant-type:token-exchange'
        );
        body.set('subject_token', accessToken);
        body.set(
          'subject_token_type',
          'urn:ietf:params:oauth:token-type:access_token'
        );
        body.set(
          'requested_token_type',
          'urn:ietf:params:oauth:token-type:access_token'
        );
        body.set('audience', 'zendesk-widget');
        body.set('scope', 'user');

        const headers = new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Basic ${btoa(`${clientId}:`)}`,
        });

        return this.http
          .post<TokenExchangeResponse>(tokenExchangeUrl, body.toString(), {
            headers,
          })
          .pipe(
            switchMap((resp) =>
              of({
                token: resp.access_token,
                isLegacy: false,
                existsInZendesk: true,
              })
            )
          );
      }),
      catchError((err) => {
        // eslint-disable-next-line no-console
        console.error('Zendesk Sirius token exchange failed', err);
        return of(null);
      })
    );
  }
}
